import { Card, Table } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { MissionRow } from '@/api/missions';
import { rowClick, rowStyle } from '@/components/rowClick';
import { SortableTh } from '@/components/SortableTh';
import { useTableSort } from '@/components/useTableSort';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';

export interface MissionsTableProps {
  missions: MissionRow[];
  /** Drone key → display name, from the catalogue. */
  droneNames: Record<string, string>;
}

export function MissionsTable({ missions, droneNames }: MissionsTableProps) {
  const { t } = useTranslation('missions');
  const { number, dateTime } = useFormat();
  const navigate = useNavigate();
  const { sorted, sortProps } = useTableSort(missions, {
    name: (m) => m.name,
    drone: (m) => droneNames[m.drone] ?? m.drone,
    area: (m) => (m.stats as { area_ha?: number }).area_ha,
    photos: (m) => (m.stats as { photos?: number }).photos,
    flightTime: (m) => (m.stats as { flight_s?: number }).flight_s,
    updated: (m) => Date.parse(m.updated_at),
    author: (m) => m.updated_by_name || m.created_by_name,
  });
  return (
    <Card p={0}>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <SortableTh {...sortProps('name')}>{t('table.name')}</SortableTh>
            <SortableTh {...sortProps('drone')}>{t('table.drone')}</SortableTh>
            <SortableTh {...sortProps('area')} ta="right">{t('table.area')}</SortableTh>
            <SortableTh {...sortProps('photos')} ta="right">{t('table.photos')}</SortableTh>
            <SortableTh {...sortProps('flightTime')} ta="right">{t('table.flightTime')}</SortableTh>
            <SortableTh {...sortProps('updated')}>{t('table.updated')}</SortableTh>
            <SortableTh {...sortProps('author')}>{t('table.author')}</SortableTh>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sorted.map((m) => {
            const stats = m.stats as { area_ha?: number; photos?: number; flight_s?: number };
            return (
              <Table.Tr key={m.public_id} style={rowStyle} onClick={rowClick(() => void navigate({ to: '/staff/missions/$missionId', params: { missionId: m.public_id } }))}>
                <Table.Td>
                  <Link to="/staff/missions/$missionId" params={{ missionId: m.public_id }} style={{ fontWeight: 600, color: brand.text, textDecoration: 'none' }}>
                    {m.name}
                  </Link>
                </Table.Td>
                <Table.Td>{droneNames[m.drone] ?? m.drone}</Table.Td>
                <Table.Td ta="right">{stats.area_ha == null ? '—' : `${number(stats.area_ha, 1)}\u00a0ha`}</Table.Td>
                <Table.Td ta="right">{stats.photos == null ? '—' : number(stats.photos)}</Table.Td>
                <Table.Td ta="right">{stats.flight_s == null ? '—' : `${number(Math.round(stats.flight_s / 60))}\u00a0min`}</Table.Td>
                <Table.Td>{dateTime(m.updated_at)}</Table.Td>
                <Table.Td>{m.updated_by_name || m.created_by_name}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
